package com.heelion.farmafreelance.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import farmafreelance.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import com.heelion.farmafreelance.PAYMENT_APP_ID
import com.heelion.farmafreelance.PAYMENT_HUB_URL
import com.heelion.farmafreelance.models.*
import com.heelion.farmafreelance.utils.TimeUtils
import com.heelion.farmafreelance.voice.VoiceIntent
import kotlinx.datetime.*

@Composable
fun HomeScreen(
    role: UserRole?,
    userName: String,
    viewModel: AuthViewModel,
    onOpenCalendar: (AnnuncioTurno) -> Unit = {},
    onViewProfile: (String, UserRole) -> Unit = { _, _ -> },
    onVoiceIntentRouted: (VoiceIntent) -> Unit = {},
    pendingIntent: VoiceIntent? = null,
    onPendingIntentConsumed: () -> Unit = {}
) {
    // La farmacia con gli infermieri attivi vede solo la professione scelta nel selettore in alto.
    val matches = viewModel.currentMatches.filter { viewModel.visibile(it.professione) }
    var expandedFilters by remember { mutableStateOf(false) }
    val filtriSelezionati = viewModel.filtriHome
    val pharmacist = viewModel.currentUserProfessionista // farmacista o infermiere
    val farmacia = viewModel.currentUserFarmacia
    val scrollState = rememberScrollState()

    var matchToCancel by remember { mutableStateOf<Pair<String, String>?>(null) }
    
    val themeColorHex = viewModel.selectedThemeColor.hex.removePrefix("0x").toLong(16)
    val themeColor = Color(themeColorHex)
    val subStatus = viewModel.subscriptionStatus

    var matchingSearchRegione by remember { mutableStateOf("") }
    var matchingSearchProvincia by remember { mutableStateOf("") }
    
    // Sincronizzazione automatica della zona di ricerca basata sul profilo (per farmacisti)
    LaunchedEffect(role, pharmacist) {
        if (role?.isProfessionista == true && pharmacist != null) {
            matchingSearchRegione = pharmacist.regioneOperativita ?: ""
            // Se il farmacista ha zone multiple, potremmo voler pre-filtrare qui, 
            // ma per ora manteniamo la regione principale del profilo
        }
    }

    val greeting = when (role) {
        UserRole.FARMACISTA -> {
            val title = when (pharmacist?.sesso) {
                "M" -> "Dott."
                "F" -> "Dott.ssa"
                else -> "Dott./Dott.ssa"
            }
            "$title ${pharmacist?.cognome ?: userName}"
        }
        UserRole.INFERMIERE -> "Inf. ${pharmacist?.cognome ?: userName}"
        UserRole.FARMACIA -> farmacia?.nomeFarmacia ?: userName
        else -> userName
    }

    // Corpo del comando vocale/testuale estratto in una funzione a sé: così sia il pulsante
    // microfono di questa schermata sia un intento già riconosciuto altrove (es. da un'altra
    // sezione, instradato qui tramite [pendingIntent]) eseguono esattamente la stessa logica.
    // Il filtro per stato è di competenza della Home (è la sua dashboard); qualunque altro
    // intento non è di competenza di questa schermata e viene rimbalzato al router centrale.
    fun gestisciIntentVocale(text: String, intent: VoiceIntent): String {
        return when (intent) {
            is VoiceIntent.FiltraTurniStato -> {
                val filtroId = when (intent.stato) {
                    VoiceIntent.StatoTurnoFiltro.PROPOSTO -> "PROPOSTO"
                    VoiceIntent.StatoTurnoFiltro.DA_CONFERMARE -> "TO_CONFIRM"
                    VoiceIntent.StatoTurnoFiltro.IN_ATTESA -> "WAITING"
                    VoiceIntent.StatoTurnoFiltro.CONFERMATO -> "CONFERMATO"
                    VoiceIntent.StatoTurnoFiltro.TUTTI -> null
                }
                viewModel.applicaDashboardFiltro(filtroId)
                if (filtroId == null) "Ho sentito: \"$text\". Rimosso il filtro, mostro tutti i turni."
                else "Ho sentito: \"$text\". Filtro applicato."
            }
            is VoiceIntent.NavigaA -> {
                if (intent.sezione == VoiceIntent.Sezione.HOME) {
                    "Sei già nella home."
                } else {
                    onVoiceIntentRouted(intent)
                    "Ho sentito: \"$text\". Ti porto alla sezione giusta..."
                }
            }
            is VoiceIntent.Unrecognized -> "Ho sentito: \"$text\". Non l'ho capito, riprova."
            else -> {
                onVoiceIntentRouted(intent)
                "Ho sentito: \"$text\". Ti porto alla sezione giusta..."
            }
        }
    }

    LaunchedEffect(pendingIntent) {
        val i = pendingIntent
        if (i != null) {
            gestisciIntentVocale("(da un'altra sezione)", i)
            onPendingIntentConsumed()
        }
    }

    Column(
        modifier = Modifier.fillMaxSize()
    ) {
        val days = subStatus.daysRemaining
        if (subStatus.isTrial && days != null) {
            TrialBanner(days)
        }
        
        Column(
            modifier = Modifier.fillMaxSize().padding(16.dp).verticalScroll(scrollState)
        ) {
            // --- CRUSCOTTO ---
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(
                    containerColor = lerp(Color.White, themeColor, 0.15f)
                ),
                shape = RoundedCornerShape(16.dp),
                border = androidx.compose.foundation.BorderStroke(
                    width = 0.5.dp,
                    color = if (themeColor == Color.White) Color.LightGray else themeColor.copy(alpha = 0.2f)
                ),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
            ) {
                Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                    // BORDO LATERALE A SINISTRA (Colore della Dashboard)
                    Box(
                        modifier = Modifier
                            .width(6.dp)
                            .fillMaxHeight()
                            .background(if (themeColor == Color.White) Color.Gray else themeColor)
                    )

                    Column(modifier = Modifier.padding(16.dp)) {
                        Text(
                            text = stringResource(Res.string.welcome),
                            style = MaterialTheme.typography.labelLarge,
                            color = if (themeColor == Color.White) Color.Gray else themeColor.copy(alpha = 0.7f),
                            letterSpacing = 1.sp
                        )
                        Text(
                            text = greeting,
                            style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                            color = when {
                                themeColor == Color.White -> Color.Black
                                themeColor == Color(0xFF2196F3) -> Color(0xFF0D47A1)
                                else -> themeColor
                            }
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            val stars = if (viewModel.userReputation.hasEnoughData) viewModel.userReputation.stars else 0.0
                            repeat(5) { index ->
                                Icon(
                                    imageVector = AppIcons.Star,
                                    contentDescription = null,
                                    tint = if (stars >= (index + 1)) Color(0xFFFFB300) else if (themeColor == Color.White) Color.LightGray else themeColor.copy(alpha = 0.2f),
                                    modifier = Modifier.size(16.dp)
                                )
                            }
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = if (viewModel.userReputation.hasEnoughData) ((stars * 10).toInt().toDouble() / 10.0).toString() else "0.0",
                                style = MaterialTheme.typography.labelSmall,
                                color = if (themeColor == Color.White) Color.Gray else themeColor.copy(alpha = 0.6f),
                                fontWeight = FontWeight.Bold
                            )
                        }

                        // RIMOSSO IL BADGE DALLA DASHBOARD PER FARLO TORNARE IN BASSO COME RICHIESTO

                        Spacer(modifier = Modifier.height(20.dp))
                        Row(modifier = Modifier.fillMaxWidth().tourBersaglio(BersaglioTour.HOME), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            val activeMatches = matches.filter { it.stato != MatchStato.RIFIUTATO && it.stato != MatchStato.ANNULLATO }
                            val myRoleTipo = role?.toCreatoreTipo()

                            data class DashItem(val label: String, val count: Int, val color: Color, val id: String)

                            val stats = listOf(
                                // Nuove proposte ricevute
                                DashItem(stringResource(Res.string.home_stats_proposed), 
                                    activeMatches.count { it.stato == MatchStato.PROPOSTO && it.propostoDa != myRoleTipo }, 
                                    Color(0xFF0E6B7A), "PROPOSTO"),
                                
                                // Turni che IO devo confermare (L'altro ha già accettato)
                                DashItem(stringResource(Res.string.home_stats_to_confirm), 
                                    activeMatches.count { it.stato == MatchStato.ACCETTATO && it.propostoDa == myRoleTipo }, 
                                    Color(0xFFE65100), "TO_CONFIRM"), // Arancione Intenso

                                // In attesa dell'altro
                                DashItem(stringResource(Res.string.status_waiting), 
                                    activeMatches.count { (it.stato == MatchStato.PROPOSTO && it.propostoDa == myRoleTipo) || (it.stato == MatchStato.ACCETTATO && it.propostoDa != myRoleTipo) }, 
                                    Color(0xFF2E7D32), "WAITING"),

                                // Definitivi
                                DashItem(stringResource(Res.string.home_stats_confirmed), 
                                    activeMatches.count { it.stato == MatchStato.CONFERMATO }, 
                                    Color(0xFFFF5722), "CONFERMATO")
                            )

                            // Insieme al suono delle notifiche lampeggiano le proposte a cui rispondere.
                            val lampeggio = rememberLampeggio(viewModel.lampeggiaProposte)
                            stats.forEach { item ->
                                val isSelected = viewModel.dashboardFiltro == item.id
                                val evidenza = if (item.count > 0 && (item.id == "PROPOSTO" || item.id == "TO_CONFIRM")) lampeggio else 0f
                                Card(
                                    modifier = Modifier.weight(1f).clickable {
                                        viewModel.updateDashboardFiltro(item.id)
                                    },
                                    colors = CardDefaults.cardColors(
                                        containerColor = androidx.compose.ui.graphics.lerp(
                                            if (isSelected) item.color.copy(alpha = 0.1f) else Color.White,
                                            item.color.copy(alpha = 0.35f), evidenza
                                        )
                                    ),
                                    shape = RoundedCornerShape(10.dp),
                                    border = if (isSelected || evidenza > 0.5f) androidx.compose.foundation.BorderStroke(2.dp, item.color) else null
                                ) {
                                    Column(modifier = Modifier.padding(vertical = 8.dp, horizontal = 2.dp).fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                                        Text(
                                            text = item.count.toString(), 
                                            style = MaterialTheme.typography.titleLarge,
                                            fontWeight = FontWeight.ExtraBold, 
                                            color = item.color
                                        )
                                        Text(
                                            text = item.label.uppercase(), 
                                            style = MaterialTheme.typography.labelSmall, 
                                            fontSize = 7.sp, // Leggermente più piccolo per far stare 4 elementi
                                            color = if (isSelected) item.color else Color.Gray,
                                            textAlign = androidx.compose.ui.text.style.TextAlign.Center, 
                                            maxLines = 2, 
                                            lineHeight = 9.sp
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Pulsante microfono centrale: capisce l'intento (cercare farmacie/farmacisti,
            // vedere i turni di una farmacia, creare/gestire un turno...) e porta l'utente
            // direttamente alla sezione giusta, dove il comando viene eseguito — non esegue nulla
            // qui sulla Home, la logica resta di competenza di ogni singola schermata.
            VoiceSearchButton(
                hint = "Chiedimi di filtrare i turni, cercare farmacie/farmacisti, creare/gestire un turno, o \"vai alla ricerca\"",
                onIntent = { text, intent -> gestisciIntentVocale(text, intent) },
                onError = {}
            )

            // --- EMERGENZA ---
            var urgentMatch by remember { mutableStateOf<Pair<Match, AnnuncioTurno>?>(null) }
            var emergencyAnnunci by remember { mutableStateOf<List<AnnuncioTurno>>(emptyList()) }

            LaunchedEffect(matches, viewModel.professioneVisibile) {
                val now = Clock.System.now()
                val candidates = matches.mapNotNull { m ->
                    val ann = viewModel.getAnnuncioByIdSuspend(m.annuncioId)
                    if (ann != null) { val start = TimeUtils.combineDateAndTime(ann.dataTurno, ann.oraInizio); m to ann.copy(dataTurno = start) } else null
                }
                urgentMatch = candidates.filter { (m, ann) ->
                    val isProp = m.propostoDa == role?.toCreatoreTipo()
                    val isUrgent = (ann.dataTurno - now).inWholeHours in -2..24
                    (isUrgent && ((m.stato == MatchStato.PROPOSTO && !isProp) || m.stato == MatchStato.ACCETTATO))
                }.minByOrNull { it.second.dataTurno }

                val allOpen = viewModel.getEmergencyAnnunci().filter { viewModel.visibile(it.professioneRichiesta) }
                emergencyAnnunci = allOpen.map { ann ->
                    val start = TimeUtils.combineDateAndTime(ann.dataTurno, ann.oraInizio)
                    ann.copy(dataTurno = start)
                }.filter { (it.dataTurno - now).inWholeHours in -2..24 }.sortedBy { it.dataTurno }
            }

            if (urgentMatch != null || emergencyAnnunci.isNotEmpty()) {
                Column(modifier = Modifier.fillMaxWidth().padding(top = 16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    urgentMatch?.let { (match, ann) ->
                        val diff = ann.dataTurno - Clock.System.now()
                        val displayTime = if (diff.inWholeMinutes < 0) stringResource(Res.string.status_in_progress) else if (diff.inWholeMinutes < 60) "${diff.inWholeMinutes}m" else "${diff.inWholeHours}h"
                        val isProp = match.propostoDa == role?.toCreatoreTipo()
                        val isWaitingMyConfirm = match.stato == MatchStato.ACCETTATO && isProp
                        val isWaitingOtherConfirm = match.stato == MatchStato.ACCETTATO && !isProp

                        Card(
                            modifier = Modifier.fillMaxWidth().clickable { if (role?.isProfessionista == true) onViewProfile(match.farmaciaId, UserRole.FARMACIA) else onViewProfile(match.farmacistaId, match.professione.ruolo) },
                            colors = CardDefaults.cardColors(containerColor = Color(0xFFFFEBEE)), 
                            shape = RoundedCornerShape(16.dp), 
                            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                        ) {
                            Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                                // BORDO A SINISTRA (Richiesta Utente)
                                Box(modifier = Modifier.width(6.dp).fillMaxHeight().background(Color.Red))
                                
                                Column(modifier = Modifier.padding(16.dp).weight(1f)) {
                                    // RIGA SUPERIORE: Nome e Prezzo (Stile Immobiliare)
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(
                                                text = (match.nomeControparte ?: "URGENTE").uppercase(), 
                                                style = MaterialTheme.typography.titleMedium, 
                                                fontWeight = FontWeight.ExtraBold, 
                                                color = Color.Red
                                            )
                                            // Indirizzo
                                            val localita = if (match.propostoDa == CreatoreTipo.FARMACIA) {
                                                "${match.indirizzoControparte ?: ""}, ${match.capControparte ?: ""} ${match.cittaControparte ?: ""}"
                                            } else {
                                                "${match.cittaControparte ?: ""}, ${match.capControparte ?: ""}"
                                            }
                                            Text(text = localita, style = MaterialTheme.typography.bodySmall, color = Color.Red.copy(alpha = 0.7f))
                                        }
                                        Text(
                                            text = "${TimeUtils.formatCurrency(ann.compenso ?: 0.0)} €", 
                                            style = MaterialTheme.typography.headlineSmall, 
                                            fontWeight = FontWeight.Black, 
                                            color = Color.Red
                                        )
                                    }

                                    Spacer(modifier = Modifier.height(12.dp))

                                    // RIGA CENTRALE: Orario e Timer
                                    Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            val ldt = ann.dataTurno.toLocalDateTime(TimeZone.currentSystemDefault())
                                            Text(text = "${ldt.dayOfWeek.toHomeScreenLocalized()} ${ldt.dayOfMonth}/${ldt.monthNumber} (${ann.oraInizio} - ${ann.oraFine})", style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold), color = Color.Black)
                                            Text(text = if (isWaitingMyConfirm) stringResource(Res.string.emergency_confirm_now) else if (isWaitingOtherConfirm) "IN ATTESA DI CONFERMA" else stringResource(Res.string.emergency_urgent_request), style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = Color.Red)
                                        }
                                        Column(horizontalAlignment = Alignment.End) {
                                            Text(text = displayTime, style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.ExtraBold, color = Color.Red)
                                            Text(text = stringResource(Res.string.time_remaining), style = MaterialTheme.typography.labelSmall, color = Color.Red.copy(alpha = 0.7f))
                                        }
                                    }

                                    Spacer(modifier = Modifier.height(16.dp))
                                    
                                    // AZIONI
                                    var isBusy by remember { mutableStateOf(false) }
                                    if (isBusy) {
                                        CircularProgressIndicator(modifier = Modifier.size(24.dp).align(Alignment.CenterHorizontally), color = Color.Red)
                                    } else if (isWaitingOtherConfirm) {
                                        Surface(modifier = Modifier.fillMaxWidth(), color = Color.Red.copy(alpha = 0.1f), shape = RoundedCornerShape(8.dp), border = androidx.compose.foundation.BorderStroke(1.dp, Color.Red)) {
                                            Text(text = stringResource(Res.string.emergency_accepted_notice), modifier = Modifier.padding(12.dp), style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = Color.Red, textAlign = androidx.compose.ui.text.style.TextAlign.Center)
                                        }
                                    } else {
                                        Button(
                                            onClick = { isBusy = true; val action = if (isWaitingMyConfirm) "CONFERMA" else "ACCETTA"; viewModel.respondToMatch(match.id ?: "smart-${ann.id}", action) { isBusy = false } },
                                            modifier = Modifier.fillMaxWidth(), colors = ButtonDefaults.buttonColors(containerColor = Color.Red), shape = RoundedCornerShape(8.dp)
                                        ) {
                                            Text(if (isWaitingMyConfirm) stringResource(Res.string.confirm_now_button) else stringResource(Res.string.accept_button), fontWeight = FontWeight.Bold)
                                        }
                                    }
                                }
                            }
                        }
                    }
                    emergencyAnnunci.forEach { ann ->
                        if (ann.id == urgentMatch?.second?.id) return@forEach
                        var isBusy by remember { mutableStateOf(false) }
                        val isAcc = matches.any { (it.annuncioId == ann.id || it.annuncioId == "smart-${ann.id}") && it.stato == MatchStato.ACCETTATO }
                        Card(
                            modifier = Modifier.fillMaxWidth().clickable { onViewProfile(ann.creatoreId, if (role?.isProfessionista == true) UserRole.FARMACIA else ann.professioneRichiesta.ruolo) },
                            colors = CardDefaults.cardColors(containerColor = Color.White), 
                            shape = RoundedCornerShape(12.dp), 
                            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                        ) {
                            Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                                // BORDO A SINISTRA (Richiesta Utente)
                                Box(modifier = Modifier.width(6.dp).fillMaxHeight().background(Color.Red.copy(alpha = 0.6f)))
                                
                                Column(modifier = Modifier.padding(16.dp).weight(1f)) {
                                    // RIGA SUPERIORE: Nome e Prezzo (Stile Immobiliare)
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(
                                                text = (ann.nomeCreatore ?: stringResource(Res.string.pharmacy_pharmacist_fallback)).uppercase(), 
                                                style = MaterialTheme.typography.titleMedium, 
                                                fontWeight = FontWeight.ExtraBold, 
                                                color = Color.Black
                                            )
                                            // La farmacia che lavora anche con gli infermieri vede di chi è il turno (simbolo e colore).
                                            if (role == UserRole.FARMACIA && viewModel.infermieriAttivi) EtichettaProfessione(ann.professioneRichiesta)
                                            ann.localitaCreatore?.takeIf { it.isNotBlank() }?.let { Text(text = it, style = MaterialTheme.typography.bodySmall, color = Color.Gray) }
                                        }
                                        Text(
                                            text = "${TimeUtils.formatCurrency(ann.compenso ?: 0.0)} €", 
                                            style = MaterialTheme.typography.headlineSmall, 
                                            fontWeight = FontWeight.Black, 
                                            color = Color.Red
                                        )
                                    }

                                    Spacer(modifier = Modifier.height(8.dp))

                                    // INFO TURNO
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        val ldt = ann.dataTurno.toLocalDateTime(TimeZone.currentSystemDefault())
                                        Icon(AppIcons.Event, contentDescription = null, tint = Color.Red, modifier = Modifier.size(16.dp))
                                        Spacer(Modifier.width(8.dp))
                                        Text(stringResource(Res.string.emergency_label, ldt.dayOfMonth, ldt.monthNumber, ann.oraInizio), fontWeight = FontWeight.Bold, color = Color.Black)
                                    }

                                    Spacer(modifier = Modifier.height(12.dp))

                                    if (isBusy) {
                                        CircularProgressIndicator(modifier = Modifier.size(24.dp).align(Alignment.CenterHorizontally), color = Color.Red)
                                    } else if (isAcc) {
                                        Surface(modifier = Modifier.fillMaxWidth(), color = Color.Red.copy(alpha = 0.1f), shape = RoundedCornerShape(8.dp)) {
                                            Text(stringResource(Res.string.emergency_accepted_notice), modifier = Modifier.padding(8.dp), style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = Color.Red, textAlign = androidx.compose.ui.text.style.TextAlign.Center)
                                        }
                                    } else {
                                        Button(
                                            onClick = { isBusy = true; viewModel.respondToMatch("smart-${ann.id}", "ACCETTA") { isBusy = false } },
                                            modifier = Modifier.fillMaxWidth(), colors = ButtonDefaults.buttonColors(containerColor = Color.Red), shape = RoundedCornerShape(8.dp)
                                        ) {
                                            Text(stringResource(Res.string.accept_button), fontWeight = FontWeight.Bold)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            // --- MATCHING SECTION ---
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(text = stringResource(Res.string.matching_title), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                Row(verticalAlignment = Alignment.CenterVertically) {
                    // Filtri con menu a tendina
                    Box {
                        IconButton(onClick = { expandedFilters = true }) {
                            Icon(
                                imageVector = AppIcons.FilterList,
                                contentDescription = "Filtra",
                                tint = if (filtriSelezionati.isNotEmpty() || matchingSearchRegione.isNotEmpty()) MaterialTheme.colorScheme.primary else Color.Gray
                            )
                        }
                        DropdownMenu(
                            expanded = expandedFilters,
                            onDismissRequest = { expandedFilters = false }
                        ) {
                            Text("FILTRO STATO", modifier = Modifier.padding(16.dp, 8.dp), style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                            val options = listOf(
                                "PROPOSTO" to Res.string.status_proposed,
                                "ACCETTATO" to Res.string.status_accepted,
                                "CONFERMATO" to Res.string.status_confirmed,
                                "WAITING" to Res.string.status_waiting
                            )
                            options.forEach { (id, resId) ->
                                val isSelected = filtriSelezionati.contains(id)
                                DropdownMenuItem(
                                    text = {
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Checkbox(checked = isSelected, onCheckedChange = null)
                                            Spacer(Modifier.width(8.dp))
                                            Text(stringResource(resId), fontSize = 14.sp)
                                        }
                                    },
                                    onClick = {
                                        viewModel.filtriHome = if (isSelected) filtriSelezionati - id else filtriSelezionati + id
                                    }
                                )
                            }
                            
                            HorizontalDivider()
                            Text("FILTRO GEOGRAFICO", modifier = Modifier.padding(16.dp, 8.dp), style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                            
                            // Selezione Regione (semplificata per non appesantire il menu)
                            DropdownMenuItem(
                                text = { Text(if(matchingSearchRegione.isEmpty()) "Tutte le Regioni" else "Regione: $matchingSearchRegione") },
                                onClick = { /* Qui potresti aprire un sotto-dialogo se servono tutte, per ora filtriamo sulla mia */ }
                            )

                            if (filtriSelezionati.isNotEmpty() || matchingSearchRegione.isNotEmpty()) {
                                HorizontalDivider()
                                DropdownMenuItem(
                                    text = { Text(stringResource(Res.string.reset_filters), color = Color.Red, fontWeight = FontWeight.Bold) },
                                    onClick = {
                                        viewModel.filtriHome = emptySet()
                                        matchingSearchRegione = ""
                                        matchingSearchProvincia = ""
                                        expandedFilters = false
                                    }
                                )
                            }
                        }
                    }
                    IconButton(onClick = { viewModel.refreshUserData() }) {
                        Icon(imageVector = AppIcons.Refresh, contentDescription = "Aggiorna", tint = MaterialTheme.colorScheme.primary)
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            val filtered = matches.filter { m ->
                if (m.stato == MatchStato.RIFIUTATO || m.stato == MatchStato.ANNULLATO) return@filter false
                if (m.id == urgentMatch?.first?.id) return@filter false
                
                // Filtro Geografico
                if (matchingSearchRegione.isNotEmpty() && m.regioneControparte != matchingSearchRegione) return@filter false
                if (matchingSearchProvincia.isNotEmpty() && m.provinciaControparte != matchingSearchProvincia) return@filter false

                val isP = m.propostoDa == role?.toCreatoreTipo()
                val isW = (m.stato == MatchStato.PROPOSTO && isP) || (m.stato == MatchStato.ACCETTATO && !isP)

                // 1. Applichiamo la Dashboard (ESCLUSIVA)
                val passDashboard = if (viewModel.dashboardFiltro == null) true else {
                    val myRoleTipo = role?.toCreatoreTipo()
                    when (viewModel.dashboardFiltro) {
                        "PROPOSTO" -> m.stato == MatchStato.PROPOSTO && m.propostoDa != myRoleTipo
                        "TO_CONFIRM" -> m.stato == MatchStato.ACCETTATO && m.propostoDa == myRoleTipo
                        "WAITING" -> (m.stato == MatchStato.PROPOSTO && m.propostoDa == myRoleTipo) || (m.stato == MatchStato.ACCETTATO && m.propostoDa != myRoleTipo)
                        "CONFERMATO" -> m.stato == MatchStato.CONFERMATO
                        else -> true
                    }
                }

                // 2. Applichiamo i Filtri Avanzati (COMBINABILI)
                val passMenu = if (filtriSelezionati.isEmpty()) true else {
                    filtriSelezionati.any { fId ->
                        when (fId) {
                            "PROPOSTO" -> m.stato == MatchStato.PROPOSTO && !isW
                            "ACCETTATO" -> m.stato == MatchStato.ACCETTATO && !isW
                            "CONFERMATO" -> m.stato == MatchStato.CONFERMATO
                            "WAITING" -> isW
                            else -> false
                        }
                    }
                }

                // Un elemento deve passare ENTRAMBI i filtri (Intersezione)
                passDashboard && passMenu
            }

            if (filtered.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().height(100.dp), contentAlignment = Alignment.Center) {
                    Text(text = stringResource(Res.string.no_matches_active), color = Color.Gray)
                }
            } else {
                // Sblocco basato su isValidPremium (controllo scadenza temporale)
                val shownMatches = if (viewModel.accessoCompleto) filtered else filtered.take(3)
                val hiddenCount = filtered.size - shownMatches.size
                
                val groupedMatches = shownMatches
                    .sortedBy { it.dataTurno }
                    .groupBy { it.dataTurno?.let { t -> TimeUtils.getMonthYear(t) } ?: "Altro" }

                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    groupedMatches.forEach { (monthYear, items) ->
                        Text(
                            text = monthYear.uppercase(),
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.ExtraBold,
                            color = if (themeColor == Color.White) Color.Black else themeColor,
                            modifier = Modifier.padding(top = 16.dp, bottom = 4.dp, start = 4.dp)
                        )
                        
                        items.forEach { m ->
                            MatchCard(
                                match = m, 
                                viewModel = viewModel, 
                                onOpenCalendar = onOpenCalendar, 
                                onViewProfile = onViewProfile, 
                                onCancelRequest = { id, act -> matchToCancel = id to act }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                        }
                    }
                    
                    if (!viewModel.accessoCompleto && hiddenCount > 0) {
                        Card(
                            modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                            colors = CardDefaults.cardColors(containerColor = Color(0xFFFFD700).copy(alpha = 0.1f)),
                            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFFFD700).copy(alpha = 0.5f))
                        ) {
                            Column(modifier = Modifier.padding(16.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                                Text(
                                    "Stai visualizzando 3 di ${filtered.size} proposte.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontWeight = FontWeight.Bold
                                )
                                Text(
                                    "Passa a Premium per sbloccare le altre $hiddenCount e confermare i tuoi turni.",
                                    style = MaterialTheme.typography.bodySmall,
                                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                                PremiumGuard(viewModel) {}
                            }
                        }
                    }
                }
            }

            // Banner persistente in basso (SPOSTATO FUORI DALL'IF/ELSE)
            if (!viewModel.accessoCompleto) {
                val uriHandler = LocalUriHandler.current
                Spacer(modifier = Modifier.height(32.dp))
                Card(
                    modifier = Modifier.fillMaxWidth().clickable {
                        val userId = viewModel.getUnivocoUtenteId() ?: ""
                        val userRole = if (role == UserRole.FARMACIA) "FARMACIA" else role?.name ?: "FARMACISTA"
                        uriHandler.openUri("$PAYMENT_HUB_URL?app=$PAYMENT_APP_ID&role=$userRole&userId=$userId")
                    },
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f)),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Star, contentDescription = null, tint = Color(0xFFB8860B))
                        Spacer(Modifier.width(16.dp))
                        Column(modifier = Modifier.weight(1f)) {
                            Text("FarmaFreelance Premium", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                            Text("Sblocca tutte le funzioni e conferma i tuoi turni senza limiti.", style = MaterialTheme.typography.labelSmall)
                        }
                        Surface(
                            color = Color(0xFFB8860B),
                            shape = RoundedCornerShape(8.dp)
                        ) {
                            Text(
                                text = "ATTIVA", 
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                fontSize = 10.sp, 
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(40.dp))
        }
    }

    matchToCancel?.let { (mId, act) ->
        AlertDialog(
            onDismissRequest = { matchToCancel = null },
            title = { Text(stringResource(Res.string.confirm_operation)) },
            text = { Text(stringResource(Res.string.cancel_match_confirm_msg)) },
            confirmButton = { Button(onClick = { viewModel.respondToMatch(mId, act); matchToCancel = null }, colors = ButtonDefaults.buttonColors(containerColor = Color.Red)) { Text(stringResource(Res.string.yes_cancel)) } },
            dismissButton = { TextButton(onClick = { matchToCancel = null }) { Text(stringResource(Res.string.cancel_button)) } }
        )
    }

    viewModel.actionErrorMessage?.let { err ->
        AlertDialog(onDismissRequest = { viewModel.actionErrorMessage = null }, title = { Text(stringResource(Res.string.attention)) }, text = { Text(err) }, confirmButton = { Button(onClick = { viewModel.actionErrorMessage = null }) { Text(stringResource(Res.string.ok_button)) } })
    }
}

@Composable
fun DayOfWeek.toHomeScreenLocalized(): String = when(this) {
    DayOfWeek.MONDAY -> stringResource(Res.string.monday)
    DayOfWeek.TUESDAY -> stringResource(Res.string.tuesday)
    DayOfWeek.WEDNESDAY -> stringResource(Res.string.wednesday)
    DayOfWeek.THURSDAY -> stringResource(Res.string.thursday)
    DayOfWeek.FRIDAY -> stringResource(Res.string.friday)
    DayOfWeek.SATURDAY -> stringResource(Res.string.saturday)
    DayOfWeek.SUNDAY -> stringResource(Res.string.sunday)
    else -> this.toString()
}

@Composable
fun MatchCard(match: Match, viewModel: AuthViewModel, onOpenCalendar: (AnnuncioTurno) -> Unit, onViewProfile: (String, UserRole) -> Unit, onCancelRequest: (String, String) -> Unit) {
    val role = viewModel.currentUserRole
    val isProp = match.propostoDa == role?.toCreatoreTipo()
    var isSel by remember(match.id, match.stato) { mutableStateOf(false) }
    var ann by remember(match.annuncioId) { mutableStateOf<AnnuncioTurno?>(null) }
    LaunchedEffect(match.annuncioId) { viewModel.getAnnuncioById(match.annuncioId) { ann = it } }
    
    val conflictDate = ann?.let { viewModel.checkConflict(it) }

    Column {
        if (conflictDate != null && match.stato != MatchStato.CONFERMATO) {
            ConflictAlert(conflictDate)
        }
        
        Card(
            modifier = Modifier.fillMaxWidth().clickable { if (role?.isProfessionista == true) onViewProfile(match.farmaciaId, UserRole.FARMACIA) else onViewProfile(match.farmacistaId, match.professione.ruolo) },
            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp), colors = CardDefaults.cardColors(containerColor = Color.White)
        ) {
            val statusColor = when (match.stato) {
                MatchStato.CONFERMATO -> Color(0xFFFF5722)
                MatchStato.ACCETTATO -> Color(0xFF2E7D32)
                else -> Color(0xFF1B5E20) // Verde Scuro predefinito (Richiesta Utente)
            }

            Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                // BORDO A SINISTRA (Richiesta Utente)
                Box(
                    modifier = Modifier
                        .width(4.dp)
                        .fillMaxHeight()
                        .background(statusColor)
                )

                Column(modifier = Modifier.padding(16.dp).weight(1f)) {
                    // RIGA SUPERIORE: Nome (Sinistra) e Prezzo (Destra) - Stile Immobiliare
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                text = if (isProp) stringResource(Res.string.your_proposal).uppercase() else (match.nomeControparte ?: "...").uppercase(),
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.ExtraBold,
                                color = if (isProp) MaterialTheme.colorScheme.primary else Color.Black
                            )
                            
                            // INDIRIZZO / LOCALITÀ (Basato sulla controparte)
                            val localita = if (role?.isProfessionista == true) {
                                "${match.indirizzoControparte ?: ""}, ${match.capControparte ?: ""} ${match.cittaControparte ?: ""}"
                            } else {
                                "${match.cittaControparte ?: ""}, ${match.capControparte ?: ""}"
                            }
                            // La farmacia che lavora anche con gli infermieri vede di chi è la proposta (simbolo e colore).
                            if (role == UserRole.FARMACIA && viewModel.infermieriAttivi) {
                                EtichettaProfessione(match.professione)
                            }
                            if (localita.length > 5) {
                                Text(
                                    text = localita,
                                    style = MaterialTheme.typography.bodySmall,
                                    color = Color.Gray,
                                    maxLines = 1
                                )
                            }
                        }
                        
                        // COMPENSO (Alto a Destra) - Grande e in Grassetto
                        ann?.let { a ->
                            Text(
                                text = "${TimeUtils.formatCurrency(a.compenso ?: 0.0)} €",
                                style = MaterialTheme.typography.headlineSmall,
                                fontWeight = FontWeight.Black,
                                color = MaterialTheme.colorScheme.primary
                            )
                        }
                    }
                    
                    Spacer(modifier = Modifier.height(8.dp))
                    
                    ann?.let { a -> 
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(AppIcons.Event, contentDescription = null, modifier = Modifier.size(16.dp), tint = Color.Gray)
                            Spacer(Modifier.width(8.dp))
                            val ldt = a.dataTurno.toLocalDateTime(TimeZone.currentSystemDefault())
                            Text(
                                text = "${ldt.dayOfWeek.toHomeScreenLocalized()} ${ldt.dayOfMonth}/${ldt.monthNumber} (${a.oraInizio} - ${a.oraFine})",
                                style = MaterialTheme.typography.bodyMedium,
                                fontWeight = FontWeight.Bold
                            )
                        }
                        if (a.quantitaMassima > 1) {
                            Column(modifier = Modifier.padding(start = 24.dp, top = 4.dp)) {
                                Text(
                                    text = "Occupazione Turno: ${a.postiOccupati}/${a.quantitaMassima}",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = Color.Gray
                                )
                                
                                // Lista altri farmacisti confermati (se sono la farmacia o un farmacista nel turno)
                                val others = viewModel.currentMatches.filter { 
                                    it.annuncioId == match.annuncioId && 
                                    it.id != match.id && 
                                    it.stato == MatchStato.CONFERMATO 
                                }
                                
                                if (others.isNotEmpty()) {
                                    Text(
                                        text = "Altri farmacisti: " + others.joinToString { it.nomeControparte ?: "..." },
                                        style = MaterialTheme.typography.labelSmall,
                                        color = MaterialTheme.colorScheme.primary,
                                        maxLines = 1,
                                        overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis
                                    )
                                }
                            }
                        }
                    }
                    
                    Spacer(modifier = Modifier.height(12.dp))

                    // RIGA INFERIORE: Pulsanti (Sinistra) e Icone/Badge (Destra) -> Invertiti o Riallineati
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Bottom) {
                        // Sezione Icone/Badge (ORA A SINISTRA rispetto alle azioni)
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            // Icona Ruolo Cerchiata (Ripristino Store)
                            val iconVector = if (match.propostoDa != CreatoreTipo.FARMACIA) AppIcons.Person else AppIcons.Store
                            
                            Surface(
                                shape = androidx.compose.foundation.shape.CircleShape,
                                color = statusColor.copy(alpha = 0.15f),
                                modifier = Modifier.size(32.dp),
                                border = androidx.compose.foundation.BorderStroke(1.dp, statusColor.copy(alpha = 0.3f))
                            ) {
                                Icon(
                                    imageVector = iconVector,
                                    contentDescription = null,
                                    tint = statusColor,
                                    modifier = Modifier.padding(6.dp).fillMaxSize()
                                )
                            }

                            Spacer(modifier = Modifier.width(8.dp))

                            // Badge Smart Match
                            if (match.id?.startsWith("smart-") == true) {
                                Surface(
                                    color = Color(0xFFE8F5E9),
                                    shape = RoundedCornerShape(4.dp)
                                ) {
                                    Text("SUGGESTED", modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp), fontSize = 8.sp, fontWeight = FontWeight.Bold, color = Color(0xFF2E7D32))
                                }
                            }
                        }

                        Spacer(modifier = Modifier.width(16.dp))

                        // Sezione Azioni (Spostata a Destra)
                        Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.CenterEnd) {
                            val matchIdToUse = match.id ?: "smart-${match.annuncioId}"
                            when (match.stato) {
                                MatchStato.PROPOSTO -> {
                                    if (!isProp) {
                                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                            Button(
                                                onClick = { if (isSel) viewModel.respondToMatch(matchIdToUse, "ACCETTA") else isSel = true }, 
                                                modifier = Modifier.height(36.dp),
                                                shape = RoundedCornerShape(8.dp)
                                            ) { 
                                                Text(if (isSel) "CONFERMA ACCETTAZIONE" else "SELEZIONA", fontSize = 10.sp, fontWeight = FontWeight.Bold) 
                                            }
                                            if (!isSel) {
                                                OutlinedButton(
                                                    onClick = { onCancelRequest(matchIdToUse, "RIFIUTA") }, 
                                                    modifier = Modifier.height(36.dp),
                                                    shape = RoundedCornerShape(8.dp)
                                                ) { 
                                                    Text("RIFIUTA", fontSize = 10.sp, fontWeight = FontWeight.Bold) 
                                                }
                                            }
                                        }
                                    } else {
                                        Text("In attesa della controparte...", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                                    }
                                }
                                MatchStato.ACCETTATO -> {
                                    if (isProp) {
                                        Button(
                                            onClick = { if (isSel) viewModel.respondToMatch(matchIdToUse, "CONFERMA") else isSel = true }, 
                                            modifier = Modifier.fillMaxWidth().height(36.dp),
                                            shape = RoundedCornerShape(8.dp)
                                        ) { 
                                            Text(if (isSel) "CONFERMA FINALE" else "SELEZIONA PER CONFERMARE", fontSize = 10.sp, fontWeight = FontWeight.Bold) 
                                        }
                                    } else {
                                        Column(horizontalAlignment = Alignment.End) {
                                            Text("In attesa di conferma finale...", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                                            Spacer(modifier = Modifier.height(4.dp))
                                            OutlinedButton(
                                                onClick = { viewModel.respondToMatch(matchIdToUse, "LIBERA") },
                                                modifier = Modifier.fillMaxWidth().height(32.dp),
                                                shape = RoundedCornerShape(8.dp)
                                            ) {
                                                Text("RITIRA ACCETTAZIONE", fontSize = 10.sp)
                                            }
                                        }
                                    }
                                }
                                MatchStato.CONFERMATO -> {
                                    Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                                        TextButton(onClick = { ann?.let { onOpenCalendar(it) } }) { Text("VEDI NEL CALENDARIO", fontSize = 11.sp) }
                                        TextButton(onClick = { onCancelRequest(matchIdToUse, "DISDICI") }, colors = ButtonDefaults.textButtonColors(contentColor = Color.Red)) { Text("DISDICI", fontSize = 11.sp) }
                                    }
                                }
                                else -> {}
                            }
                        }
                    }
                }
            }
        }
    }
}

fun UserRole.toCreatoreTipo(): CreatoreTipo = comeCreatore ?: CreatoreTipo.FARMACISTA
